import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import AOS from 'aos'
import { NAV_LINKS, ROUTES, SITE } from '@/constants'
import Button from '@ui/Button'
import MetaInfo from '@common/MetaInfo'
import Breadcrumb from '@common/Breadcrumb'
/* ── Extra standalone pages not in NAV_LINKS ── */
const QUICK_LINKS = [
  //  { label: 'Apply For Job',      href: ROUTES.APPLY_FOR_JOB },
  { label: 'Terms of Use',      href: ROUTES.TERMS_OF_USE },
  { label: 'Privacy Policy',      href: ROUTES.PRIVACY_POLICY },
  { label: 'Disclaimer',      href: ROUTES.DISCLAIMER },
  { label: 'Sales Enquiry', href: ROUTES.SALES_ENQUIRY },
]

/* ── Icon map per nav section label ── */
const SECTION_ICONS = {
  'Consulting':      'fa-solid fa-lightbulb',
  'IT Services':     'fa-solid fa-layer-group',
  'AI/ML Solutions': 'fa-solid fa-brain',
  'Know Us':         'fa-solid fa-users',
  'Portfolio':       'fa-solid fa-briefcase',
  'Quick Links':     'fa-solid fa-link',
}

/* ── Single leaf link ── */
function LeafLink({ label, href }) {
  return (
    <li className="sitemap-leaf">
      <Link to={href} className="sitemap-leaf-link">
        <i className="fa-solid fa-arrow-right sitemap-leaf-arrow" aria-hidden="true" />
        <span>{label}</span>
      </Link>
    </li>
  )
}

/* ── Sub-group heading + its items (e.g. "Design & Product") ── */
function SubGroup({ heading, items }) {
  return (
    <li className="sitemap-subgroup">
      <div className="sitemap-subgroup-connector" aria-hidden="true" />
      <div className="sitemap-subgroup-label">
        <span>{heading}</span>
      </div>
      <ul className="sitemap-subgroup-items" role="list">
        {items.map((item) => (
          <LeafLink key={item.href} label={item.label} href={item.href} />
        ))}
      </ul>
    </li>
  )
}

/* ── One top-level section branch ── */
function SitemapSection({ navItem, index }) {
  const iconClass = SECTION_ICONS[navItem.label] || 'fa-solid fa-folder'
  const hasGroups = navItem.children?.some((c) => c.items)

  return (
    <div
      className="sitemap-branch"
      data-aos="fade-up"
      data-aos-delay={index * 80}
    >
      {/* Section header node */}
      <div className="sitemap-section-node">
        <div className="sitemap-node-line" aria-hidden="true" />
        <div className="sitemap-section-badge">
          <span className="sitemap-section-icon" aria-hidden="true">
            <i className={iconClass} />
          </span>
          <span className="sitemap-section-label">
            {/* If parent has a real href, make it clickable */}
            {navItem.href
              ? <Link to={navItem.href}>{navItem.label}</Link>
              : navItem.label
            }
          </span>
        </div>
      </div>

      {/* Children */}
      <ul className="sitemap-children" role="list">
        {hasGroups
          ? /* Grouped structure — IT Services, Consulting (multi-col) */
            navItem.children.map((group) =>
              group.heading
                ? <SubGroup key={group.heading} heading={group.heading} items={group.items} />
                : group.items?.map((item) => (
                    <LeafLink key={item.href} label={item.label} href={item.href} />
                  ))
            )
          : /* Flat structure — Know Us, Portfolio */
            navItem.children.map((child) => (
              <LeafLink key={child.href} label={child.label} href={child.href} />
            ))
        }
      </ul>
    </div>
  )
}

/* ── Page ── */
export default function SiteMapPage() {
  useEffect(() => {
    AOS.refresh()
    window.scrollTo(0, 0)
  }, [])

  /* Filter out Home from NAV_LINKS — Home is shown as the root node */
  const sections = NAV_LINKS.filter((l) => l.label !== 'Home')

  return (
    <main id="main-content" className="page-wrapper">
      <MetaInfo
        title={`Sitemap | ${SITE.fullName}`}
        description="A complete overview of all pages available on the CruxBytes website."
      />

      {/* ── Hero ── */}
      <div className="page-hero page-hero-small" style={{boxShadow:'none'}}>
        <div  className="bg-grid bg-grid--fill"  aria-hidden="true"    />
        <div className="container">
          <Breadcrumb />
          <h1 data-aos="fade-up" data-aos-delay={100}> Sitemap </h1>
           <p className="page-subtitle page-subtitle-small" data-aos="fade-up" data-aos-delay={200}>
            Navigation journey of our website.
          </p>
        </div>
      </div>

      {/* ── Tree ── */}
      <section
        className="sitemap-section"
        aria-label="Site structure"
      >
        <div className="container">

          {/* Root node — Home */}
          <div className="sitemap-root" data-aos="fade-up">
            <Link to={ROUTES.HOME} className="sitemap-root-node" aria-label="Home — go to homepage">
              <i className="fa-solid fa-house" aria-hidden="true" />
              <span>Home</span>
            </Link>
            <div className="sitemap-root-stem" aria-hidden="true" />
          </div>

          {/* Section branches */}
          <div className="sitemap-tree" role="navigation" aria-label="Full site structure">
            {sections.map((navItem, i) => (
              <SitemapSection key={navItem.label} navItem={navItem} index={i} />
            ))}

            {/* Quick Links branch */}
            <div className="sitemap-branch" data-aos="fade-up" data-aos-delay={sections.length * 80}>
              <div className="sitemap-section-node">
                <div className="sitemap-node-line" aria-hidden="true" />
                <div className="sitemap-section-badge">
                  <span className="sitemap-section-icon" aria-hidden="true">
                    <i className="fa-solid fa-link" />
                  </span>
                  <span className="sitemap-section-label">Quick Links</span>
                </div>
              </div>
              <ul className="sitemap-children" role="list">
                {QUICK_LINKS.map((link) => (
                  <LeafLink key={link.href} label={link.label} href={link.href} />
                ))}
              </ul>
            </div>
          </div>

        </div>
      </section>
    </main>
  )
}